import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Container, Section, Eyebrow, DisplayHeading, Reveal } from "@/components/kit";

const ROLES = [
  { id: "ciso", label: "CISO", line: "Find AI vulnerabilities before attackers do.", detail: "Red-team AI systems in a controlled environment and turn findings into evidence-backed remediation." },
  { id: "caio", label: "Chief AI Officer", line: "Know what AI exists — and how much you can trust it.", detail: "See AI systems, models and agents alongside their risks, controls and assurance state." },
  { id: "gov", label: "AI Governance", line: "Turn governance requirements into accountable controls.", detail: "Connect policies, frameworks, risks, controls, owners and evidence in one traceable system." },
  { id: "risk", label: "Risk & Compliance", line: "See where AI risk exists and what is being done about it.", detail: "Trace risk to controls, evidence, ownership and assurance status." },
  { id: "audit", label: "Internal Audit", line: "Trace every assurance claim back to evidence.", detail: "Review the evidence lineage behind controls, assessments and assurance outcomes." },
  { id: "product", label: "AI Product Company", line: "Prove your AI is governed before customers ask.", detail: "Give enterprise buyers verifiable assurance without exposing confidential internal evidence." },
];

export default function Audience() {
  const [active, setActive] = useState(ROLES[0].id);
  const role = ROLES.find((r) => r.id === active);
  return (
    <Section id="audience" className="border-t border-line">
      <Container>
        <Reveal>
          <Eyebrow tone="brand">Who it’s for</Eyebrow>
        </Reveal>
        <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl md:text-5xl">
          Built for the people accountable for AI.
        </DisplayHeading>

        <div className="mt-8 grid gap-6 sm:mt-12 sm:gap-8 lg:grid-cols-[1fr_1.3fr] lg:items-center">
          <div className="flex flex-wrap gap-3 lg:flex-col lg:items-start">
            {ROLES.map((r) => (
              <button
                key={r.id}
                data-testid={`audience-role-${r.id}`}
                onMouseEnter={() => setActive(r.id)}
                onClick={() => setActive(r.id)}
                className={`group inline-flex min-h-[40px] items-center gap-2.5 rounded-full border px-4 py-2 text-left text-[13px] font-medium transition-colors duration-300 sm:gap-3 sm:px-5 sm:py-2.5 sm:text-sm ${
                  active === r.id ? "border-brand/50 bg-brand/10 text-content" : "border-line text-content-muted hover:border-brand/50 hover:text-content"
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${active === r.id ? "bg-brand-soft" : "bg-line-bright"}`} />
                {r.label}
              </button>
            ))}
          </div>

          <div className="relative min-h-[200px] overflow-hidden rounded-lg border border-line bg-panel-raised p-6 sm:min-h-[220px] sm:p-10">
            <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-brand/[0.05] blur-3xl" />
            <AnimatePresence mode="wait">
              <motion.div
                key={role.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-content-faint">{role.label}</div>
                <h3 className="mt-3 font-display text-[22px] font-medium leading-snug text-content sm:mt-4 sm:text-3xl">{role.line}</h3>
                <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-content-muted">{role.detail}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Container>
    </Section>
  );
}
